export const AI_SETUP_CHOICES = [
  { title: "Model", body: "The trained engine: what can it reason about or generate? Compare its result on your Job." },
  { title: "Harness", body: "The surrounding software: how are context, tool calls, state, permissions, and stopping rules handled?" },
  { title: "Form factor", body: "Where you work and review: chat, IDE, file workspace, browser, or an embedded service." },
];
export const AI_PILOT_STEPS = [
  { title: "Hold the work constant", body: "Compare the current process and the proposed AI workflow using the same inputs, permissions, selected Job, and acceptance rubric. Include missing information and failure cases." },
  { title: "Count the whole effort", body: "Record accepted-result quality, review and rework effort, elapsed time, retries, and total cost including tool calls." },
  { title: "Set the decision first", body: "Name an owner and review date. Agree on minimum quality and acceptable effort before seeing results. Proceed, revise, or stop against those criteria." },
];
export interface AIGlossaryGroup {
  title: string;
  terms: Array<{ term: string; definition: string }>;
}

// Two slides, using the four groups and concise definitions from the supplied glossary.
export const AI_FOUNDATION_GLOSSARY: Record<string, AIGlossaryGroup[]> = {
  "ai-system-map": [
    {
      title: "AI basics",
      terms: [
        { term: "Generative AI", definition: "AI that creates text, images, audio, or code from learned patterns." },
        { term: "Model / LLM", definition: "A model is the trained engine. An LLM is a large language model." },
        { term: "Training / inference", definition: "Training changes the model. Inference uses it to produce a result." },
        { term: "Input / output", definition: "What you supply to AI; what it produces for you to review." },
      ],
    },
    {
      title: "Better requests",
      terms: [
        { term: "Prompt", definition: "Your instructions: task, audience, constraints, and desired result." },
        { term: "Context", definition: "Information available at this step: instructions, history, examples, and sources." },
        { term: "Tokens / context window", definition: "Tokens are chunks of content. The context window limits what fits at once." },
        { term: "Grounding / RAG", definition: "Grounding uses sources. RAG (retrieval-augmented generation) fetches relevant material to inform an answer." },
      ],
    },
  ],
  "ai-tool-harness-skill": [
    {
      title: "From chat to action",
      terms: [
        { term: "Tool / connector", definition: "A tool reads data or takes action; a connector links an app or data source." },
        { term: "Skill", definition: "A reusable playbook of instructions and resources. It does not grant access." },
        { term: "Harness", definition: "Software that coordinates model calls, context, tools, and configured permissions." },
        { term: "Agent", definition: "A system that uses a model and tools across multiple steps toward an objective." },
      ],
    },
    {
      title: "Judgment & control",
      terms: [
        { term: "Hallucination", definition: "A response that sounds plausible but contains false or unsupported information." },
        { term: "Prompt injection", definition: "Instructions in untrusted content that try to redirect the AI." },
        { term: "Human in the loop", definition: "A person checks work and approves actions at defined checkpoints." },
        { term: "Evaluation", definition: "Testing results against source evidence and clear success criteria." },
      ],
    },
  ],
};

export const AI_FOUNDATION_ANCHOR_ALIASES: Record<string, string> = {
  "ai-input-context": "ai-system-map",
  "ai-output-review": "ai-tool-harness-skill",
};

export const AI_TEACH_BACK = [
  "The brief invents a deadline. Was the actual deadline in the supplied context, and did you check the output against the source?",
  "A calendar is connected. What evidence shows the tool retrieved its entries and the harness supplied them to the model?",
  "The Skill says read only. Which software permission or control actually prevents a write?",
];
